import Container from '@brands/Components/Container/Container';
import PhoneInput from '@brands/Components/Inputs/MuiTelInput/MuiTelInput';
import CustomTextField from '@brands/Components/Inputs/MuiTextField/TextField';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import ChangePasswordModal from '@brands/Dashboard/Sidenav/SidenavModals/ChangePasswordModal/ChangePasswordModal';
import { confirmAvatarMedia } from '@brands/services/identity/confirmAvatarMedia';
import { createBasicInformation } from '@brands/services/identity/createBasicInformation';
import { deleteAvatar } from '@brands/services/identity/deleteAvatar';
import { getCurrentProfile } from '@brands/services/identity/getCurrentProfile';
import { getUserAvatar } from '@brands/services/identity/getUserAvatar';
import { registerAvatarMedia } from '@brands/services/identity/registerAvatarMedia';
import { saveBasicInformation } from '@brands/services/identity/saveBasicInformation';
import { BasicInfo, UserProfile } from '@brands/services/identity/types/UserProfile';
import { UpdateMe, updateMe } from '@brands/services/identity/updateMe';
import { updateVerifiedEmail } from '@brands/services/identity/updateUserEmail';
import { selectAuth } from '@brands/store/selectors/auth';
import { setUser } from '@brands/store/slices/authSlice';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { isValidEmail, isValidName, isValidZipCode } from '@brands/Utils/validation';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@mui/material';
import { E164Number, parsePhoneNumber } from 'libphonenumber-js';
import { isEqual } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import ProfileImage from '../../../Components/ProfileImage/ProfileImage';
import { useAppDispatch, useAppSelector } from '../../../hooks/useReduxHook';
import { defaultUserValues } from '../../../services/cases/types/defaultUserValues';
import { RegisterMediaPayload } from '../../../services/communication/registersConversationMedia';
import styles from '../styles.module.scss';
import profileStyles from './styles.module.scss';

type SuperAdminProfileForm = {
  profile_img: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  zip_code: string;
};

const validationSchema = Yup.object().shape({
  profile_img: Yup.string(),
  first_name: Yup.string()
    .required('First name is required')
    .test('valid-first-name', 'Enter Valid First Name', (value) => !value || isValidName(value)),
  middle_name: Yup.string(),
  last_name: Yup.string()
    .required('Last name is required')
    .test('valid-last-name', 'Enter Valid Last Name', (value) => !value || isValidName(value)),
  email: Yup.string()
    .required('Email is required')
    .test('valid-email', 'Enter Valid Email', (value) => !value || isValidEmail(value)),
  phone_number: Yup.string().test('valid-us-phone', 'Enter Valid Phone Number', (value) => {
    if (!value) return true;
    return parsePhoneNumber(value, 'US')?.isValid() ?? false;
  }),
  zip_code: Yup.string().test('valid-zip', 'Valid Zip Code is required', (value) => {
    if (!value) return true;
    return isValidZipCode(value);
  }),
});

const mapUserToForm = (user: UserProfile): SuperAdminProfileForm => ({
  profile_img: '',
  first_name: user.basic_info?.first_name || '',
  middle_name: user.basic_info?.middle_name || '',
  last_name: user.basic_info?.last_name || '',
  email: user.email || '',
  phone_number: user.phone_number || '',
  zip_code: user.address?.zip_code || '',
});

const SuperAdminMyProfile = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector(selectAuth);
  const [thisUser, setThisUser] = useState<UserProfile>(userInfo || defaultUserValues);
  const [loading, setLoading] = useState(true);
  const [image, setImage] = useState<string>();
  const [currentAvatarURL, setCurrentAvatarURL] = useState<string>('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPayload, setAvatarPayload] = useState<RegisterMediaPayload | null>(null);
  const [avatarAction, setAvatarAction] = useState<string>('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [formSnapshot, setFormSnapshot] = useState<SuperAdminProfileForm>({} as SuperAdminProfileForm);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const imageObjectUrlRef = useRef<string | null>(null);

  const {
    handleSubmit,
    setValue,
    formState: { errors },
    control,
  } = useForm<SuperAdminProfileForm>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const watchedValues = useWatch({ control });

  const hasChanges = !isEqual(formSnapshot, watchedValues) || avatarAction !== '';

  const triggerProfileAvatar = React.useCallback(
    (avatarUrl: string) => {
      setValue('profile_img', avatarUrl, { shouldValidate: true });
    },
    [setValue],
  );

  useEffect(() => {
    (async (): Promise<void> => {
      try {
        const profile = await getCurrentProfile();
        setThisUser(profile);
      } catch (error) {
        displayErrorDetails(error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!loading && thisUser) {
      const mapped = mapUserToForm(thisUser);
      (Object.entries(mapped) as [keyof SuperAdminProfileForm, string][]).forEach(([key, val]) => {
        setValue(key, val);
      });
      setFormSnapshot(mapped);
    }
  }, [loading, thisUser, setValue]);

  useEffect(() => {
    if (image) triggerProfileAvatar(image);
  }, [image, triggerProfileAvatar]);

  useEffect(() => {
    return () => {
      if (imageObjectUrlRef.current) {
        URL.revokeObjectURL(imageObjectUrlRef.current);
      }
    };
  }, []);

  const onInvalid = (errorsList: Record<string, { message?: string }>): void => {
    const error = Object.values(errorsList)[0];
    if (error?.message) {
      toast.error(error.message);
    }
  };

  const callSaveProfile = async (profileData: SuperAdminProfileForm): Promise<void> => {
    setIsSavingProfile(true);
    try {
      if (avatarPayload && avatarFile && avatarAction === 'Upload') {
        const res = await registerAvatarMedia(avatarPayload);
        const mediaId = res.files[0].upload_id;
        await fetch(res.files[0].upload_url, {
          method: 'PUT',
          body: avatarFile,
          headers: { 'content-type': avatarFile.type },
        });
        await confirmAvatarMedia(mediaId, thisUser.id);
        if (imageObjectUrlRef.current) {
          URL.revokeObjectURL(imageObjectUrlRef.current);
          imageObjectUrlRef.current = null;
        }
        const avatarUrl = getUserAvatar(thisUser.id);
        setImage(avatarUrl);
        setCurrentAvatarURL(avatarUrl);
        triggerProfileAvatar(avatarUrl);
      } else if (avatarAction === 'Remove') {
        await deleteAvatar(thisUser.id);
        setImage(undefined);
        setCurrentAvatarURL('');
        triggerProfileAvatar('');
      }

      let updatedUser: UserProfile = thisUser;

      const basicInfoPayload: BasicInfo = {
        first_name: profileData.first_name,
        middle_name: profileData.middle_name || undefined,
        last_name: profileData.last_name,
      };

      const basicInfoChanged =
        thisUser.basic_info?.first_name !== basicInfoPayload.first_name ||
        (thisUser.basic_info?.middle_name || '') !== (basicInfoPayload.middle_name || '') ||
        thisUser.basic_info?.last_name !== basicInfoPayload.last_name;

      if (basicInfoChanged) {
        if (thisUser.basic_info !== null) {
          updatedUser = await saveBasicInformation(basicInfoPayload, thisUser.id);
        } else {
          updatedUser = await createBasicInformation(basicInfoPayload, thisUser.id);
        }
      }

      if (thisUser.email !== profileData.email) {
        updatedUser = await updateVerifiedEmail(thisUser.id, { email: profileData.email });
      }

      const normalizedPhone = profileData.phone_number
        ? (parsePhoneNumber(profileData.phone_number, 'US')?.format('E.164') ?? '')
        : '';

      const meChanged =
        (!!normalizedPhone && thisUser.phone_number !== normalizedPhone) ||
        profileData.zip_code !== (thisUser.address?.zip_code || '');

      if (meChanged) {
        const updatedMePayload: UpdateMe = {
          ...(normalizedPhone && { phone_number: normalizedPhone }),
          address: {
            ...thisUser.address,
            zip_code: profileData.zip_code || thisUser.address?.zip_code,
          },
        };
        updatedUser = await updateMe(updatedMePayload, thisUser.id);
      }

      setThisUser(updatedUser);
      dispatch(setUser({ userInfo: updatedUser }));

      const newSnapshot = mapUserToForm(updatedUser);
      setFormSnapshot(newSnapshot);
      setAvatarAction('');
      toast.success('Your changes have been saved');
    } catch (error) {
      displayErrorDetails(error);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const onSubmit = async (profileData: SuperAdminProfileForm): Promise<void> => {
    await callSaveProfile(profileData);
  };

  const setTextValue = (e: React.ChangeEvent<HTMLInputElement>, name: string): void => {
    setValue(name as keyof SuperAdminProfileForm, e.target.value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  return (
    <Container
      patientDashboard
      childClassName={styles.dashboard}
      isSidenavVisible
      childrenStyle={{ alignItems: 'flex-start' }}
    >
      <div className={profileStyles.profileContainer}>
        <form className={profileStyles.formWrapper} onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <div className={profileStyles.avatarRow}>
            <ProfileImage
              reviewProfile={false}
              id={undefined}
              setCurrentAvatarURL={setCurrentAvatarURL}
              setAvatarFile={setAvatarFile}
              setAvatarPayload={setAvatarPayload}
              setAvatarAction={setAvatarAction}
              triggerProfileAvatar={triggerProfileAvatar}
              setImage={setImage}
              image={image}
              errors={errors}
              size="72px"
              isUpload={false}
            />
            <Button
              variant="text"
              component="label"
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '15px',
                borderRadius: '32px',
                color: 'var(--primary-color)',
              }}
            >
              Upload profile picture
              <input
                type="file"
                hidden
                accept=".png,.jpg,.jpeg,.jfif,.pjpeg,.pjp,.heic"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setAvatarAction('Upload');
                    setImage(() => {
                      if (imageObjectUrlRef.current) {
                        URL.revokeObjectURL(imageObjectUrlRef.current);
                      }
                      const objectUrl = URL.createObjectURL(file);
                      imageObjectUrlRef.current = objectUrl;
                      return objectUrl;
                    });
                    setAvatarPayload({
                      files: [{ filename: file.name, mimetype: file.type }],
                    });
                    setAvatarFile(file);
                  }
                }}
              />
            </Button>
          </div>

          <div className={profileStyles.nameRow}>
            <CustomTextField
              name="first_name"
              label="First name"
              required
              value={watchedValues.first_name || ''}
              onChange={(e) => setTextValue(e, 'first_name')}
              errors={errors}
            />
            <CustomTextField
              name="middle_name"
              label="Middle name (optional)"
              value={watchedValues.middle_name || ''}
              onChange={(e) => setTextValue(e, 'middle_name')}
              errors={errors}
            />
            <CustomTextField
              name="last_name"
              label="Last name"
              required
              value={watchedValues.last_name || ''}
              onChange={(e) => setTextValue(e, 'last_name')}
              errors={errors}
            />
          </div>

          <CustomTextField
            name="email"
            label="Email address"
            required
            value={watchedValues.email || ''}
            onChange={(e) => setTextValue(e, 'email')}
            errors={errors}
          />

          <div className={profileStyles.contactRow}>
            <Controller
              name="phone_number"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  name="phone_number"
                  label="Phone number"
                  required
                  value={field.value as E164Number}
                  onChange={(val) => {
                    setValue('phone_number', val as string, {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    });
                  }}
                  errors={errors}
                  ref={phoneInputRef}
                />
              )}
            />
            <CustomTextField
              name="zip_code"
              label="Zip code"
              value={watchedValues.zip_code || ''}
              onChange={(e) => setTextValue(e, 'zip_code')}
              errors={errors}
            />
          </div>


          <div className={profileStyles.buttonRow}>
            <Button
              type="submit"
              variant="contained"
              className={profileStyles.saveButton}
              disabled={!hasChanges || isSavingProfile}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '15px',
                borderRadius: '32px',
                px: '22px',
                py: '8px',
                backgroundColor: 'var(--primary-color)',
                '&:hover': { backgroundColor: '#245fa0' },
                '&.Mui-disabled': { backgroundColor: '#bdbdbd', color: '#fff' },
              }}
            >
              Save changes
            </Button>
            <Button
              type="button"
              variant="text"
              onClick={() => setChangePasswordOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '15px',
                borderRadius: '32px',
                color: '#d32f2f',
                py: '8px',
                '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.04)' },
              }}
            >
              Reset password
            </Button>
          </div>
        </form>
      </div>

      <ChangePasswordModal
        isOpen={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        email={thisUser.email || userInfo?.email || ''}
      />
    </Container>
  );
};

export default SuperAdminMyProfile;
